export enum LogLevel {
  ERROR = "error",
  WARNING = "warning",
}

export abstract class AggregationError extends Error {
  aggregatorError?: string;
  chemicalId?: string;
  chemicalName?: string;
  logLevel: LogLevel;
  /**
   * Set logLevel to LogLevel.WARNING to log as a warning instead of an error.
   */
  constructor(message: string, logLevel: LogLevel = LogLevel.ERROR) {
    super(message);
    this.name = this.constructor.name;
    this.logLevel = logLevel;
  }

  /**
   * Names the chemical on an error that was thrown further down, where the
   * chemical wasn't in scope — `convert` only knows the two units.
   *
   * Subclasses whose `aggregatorError` names the chemical override this so the
   * persisted message picks the name up as well. Assigning `chemicalId` and
   * `chemicalName` directly instead leaves the user reading the anonymous
   * variant of the message while the attribution is only in the fields.
   */
  attributeToChemical(
    chemicalId: string | null | undefined,
    chemicalName?: string | null | undefined
  ): void {
    this.chemicalId = chemicalId ?? undefined;
    this.chemicalName = chemicalName ?? undefined;
  }
}

export class MissingDensity extends AggregationError {
  constructor(
    private readonly sourceUnit: string,
    private readonly targetUnit: string,
    chemicalId?: string,
    chemicalName?: string
  ) {
    super("Conversions between mass and volume require a density.");
    // create the RFC record for these chemicals
    this.logLevel = LogLevel.WARNING;
    this.attributeToChemical(chemicalId, chemicalName);
  }

  override attributeToChemical(
    chemicalId: string | null | undefined,
    chemicalName?: string | null | undefined
  ): void {
    super.attributeToChemical(chemicalId, chemicalName);
    // Keyed on the name, not the id: a caller can know which chemical failed
    // without having its name, and "for chemical: undefined" reads worse than
    // leaving it out.
    if (this.chemicalName) {
      this.aggregatorError = `We were unable to aggregate this chemical because we cannot convert ${this.sourceUnit} to ${this.targetUnit} for chemical: ${this.chemicalName} without a density.`;
    } else {
      this.aggregatorError = `We were unable to aggregate this chemical because we cannot convert ${this.sourceUnit} to ${this.targetUnit} without a density.`;
    }
  }
}

export class UnsupportedUnitConversion extends AggregationError {
  constructor(
    private readonly sourceUnit: string,
    private readonly targetUnit: string,
    chemicalId?: string,
    chemicalName?: string
  ) {
    super(`Conversions from ${sourceUnit} to ${targetUnit} are currently not supported.`);
    this.attributeToChemical(chemicalId, chemicalName);
  }

  override attributeToChemical(
    chemicalId: string | null | undefined,
    chemicalName?: string | null | undefined
  ): void {
    super.attributeToChemical(chemicalId, chemicalName);
    // See MissingDensity: keyed on the name so a nameless attribution doesn't
    // render "for chemical: undefined".
    if (this.chemicalName) {
      this.aggregatorError = `We were unable to aggregate this chemical because we cannot accurately convert ${this.sourceUnit} to ${this.targetUnit} at this time for chemical: ${this.chemicalName}.`;
    } else {
      // create the RFC record for these chemicals
      this.aggregatorError = `We were unable to aggregate this chemical because we cannot accurately convert ${this.sourceUnit} to ${this.targetUnit} at this time.`;
    }
  }
}

export class CouldNotDetermineUnit extends AggregationError {
  constructor(missingUnit: "from" | "to") {
    super(`Unable to determine ${missingUnit}Unit`);
    this.logLevel = LogLevel.WARNING;
    this.aggregatorError = `We were unable to aggregate this chemical because we cannot determine the unit to convert ${missingUnit}.`;
  }
}

/**
 * Leads every `aggregationErrorMessage` produced by
 * `ReportingUnitOverrideNotApplied`, so change detection can tell this message
 * apart from a message that means the chemical failed to aggregate at all.
 */
export const REPORTING_UNIT_OVERRIDE_NOT_APPLIED_PREFIX = "The reporting-unit override to";

/** True when an `aggregationErrorMessage` is a refused reporting-unit override. */
export function isReportingUnitOverrideNotAppliedMessage(
  message: string | null | undefined
): boolean {
  return message?.startsWith(REPORTING_UNIT_OVERRIDE_NOT_APPLIED_PREFIX) ?? false;
}

/**
 * A staff reporting-unit override exists but aggregation cannot honour it.
 *
 * The resolver refuses to save an override whose conversion cannot be
 * performed, so this is reached only when the data changes underneath one that
 * was valid when it was saved — most plausibly a chemical whose catalog density
 * is later cleared. The chemical keeps reporting in its aggregated unit and
 * this message tells staff why the unit they chose is not the unit they see.
 *
 * A warning rather than an error: nothing is wrong with the chemical's
 * inventory, and every reported figure is still correct.
 */
export class ReportingUnitOverrideNotApplied extends AggregationError {
  constructor({
    targetUnit,
    sourceUnit,
    reason,
  }: {
    /** Pretty-printed, because this string is shown to staff. */
    targetUnit: string;
    sourceUnit: string;
    reason: string;
  }) {
    super(`Could not apply the ${targetUnit} reporting-unit override: ${reason}`);
    this.logLevel = LogLevel.WARNING;
    this.aggregatorError =
      `${REPORTING_UNIT_OVERRIDE_NOT_APPLIED_PREFIX} ${targetUnit} could not be applied, ` +
      `so this chemical is still reported in ${sourceUnit}. ${reason}`;
  }
}
